import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Collapse,
  TextField,
  InputAdornment,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PersonIcon from '@mui/icons-material/Person';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import InventoryIcon from '@mui/icons-material/Inventory';
import GroupsIcon from '@mui/icons-material/Groups';
import SchoolIcon from '@mui/icons-material/School';
import MessageIcon from '@mui/icons-material/Message';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import LogoutIcon from '@mui/icons-material/Logout';
import HistoryIcon from '@mui/icons-material/History';
import SearchIcon from '@mui/icons-material/Search';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import SettingsIcon from '@mui/icons-material/Settings';
import { useAuth } from '../../contexts/AuthContext';
import Breadcrumbs from './Breadcrumbs';
import { AdminTour, useAdminTour, TourHelpButton } from '../AppTour/AppTour';
import StaffNotificationListener from '../StaffNotificationListener';
import SystemNoticeBanner from '../SystemNoticeBanner';
import { tokens } from '../../theme/tokens';

const drawerWidth = 232;

const menuSections = [
  {
    label: 'Overview',
    items: [
      { text: 'Dashboard', icon: <DashboardIcon fontSize="small" />, path: '/dashboard', exact: true, permission: null },
      { text: 'Audit Log', icon: <HistoryIcon fontSize="small" />, path: '/dashboard/audit', permission: 'audit' },
    ],
  },
  {
    label: 'Registry',
    items: [
      {
        text: 'Members',
        icon: <PersonIcon fontSize="small" />,
        permission: 'members',
        children: [
          { text: 'Members List', path: '/dashboard/members' },
          { text: 'Verify Member', path: '/dashboard/verify-member' },
        ],
      },
      {
        text: 'HR',
        icon: <GroupsIcon fontSize="small" />,
        permission: 'hr',
        children: [
          { text: 'Positions', path: '/dashboard/positions' },
          { text: 'Executives', path: '/dashboard/executives' },
          { text: 'Elections', path: '/dashboard/hr/elections' },
          { text: 'Events', path: '/dashboard/events' },
          { text: 'Staff', path: '/dashboard/staff' },
          { text: 'Group', path: '/dashboard/group' },
        ],
      },
    ],
  },
  {
    label: 'Operations',
    items: [
      {
        text: 'Finance',
        icon: <AccountBalanceIcon fontSize="small" />,
        permission: 'finance',
        children: [
          { text: 'Income', path: '/dashboard/income' },
          { text: 'Income Categories', path: '/dashboard/income-categories' },
          { text: 'Expenditure', path: '/dashboard/expenditure' },
          { text: 'Expenditure Categories', path: '/dashboard/expenditure-categories' },
          { text: 'Invoices', path: '/dashboard/invoices' },
        ],
      },
      {
        text: 'Asset',
        icon: <InventoryIcon fontSize="small" />,
        permission: 'assets',
        children: [
          { text: 'Asset Categories', path: '/dashboard/asset-categories' },
          { text: 'Asset', path: '/dashboard/asset' },
        ],
      },
      {
        text: 'Education',
        icon: <SchoolIcon fontSize="small" />,
        permission: 'education',
        children: [
          { text: 'Course Categories', path: '/dashboard/course-categories' },
          { text: 'Course Types', path: '/dashboard/course-types' },
          { text: 'Courses', path: '/dashboard/courses' },
          { text: 'Exams', path: '/dashboard/exams' },
          { text: 'Exam Candidates', path: '/dashboard/exam-candidates' },
          { text: 'Exam Marks', path: '/dashboard/exam-marks' },
        ],
      },
      {
        text: 'Communication',
        icon: <MessageIcon fontSize="small" />,
        path: '/dashboard/communication',
        permission: 'communication',
      },
    ],
  },
  {
    label: 'Insight',
    items: [
      {
        text: 'Reports',
        icon: <AssessmentIcon fontSize="small" />,
        permission: 'reports',
        children: [
          { text: 'General Report', path: '/dashboard/general-report' },
          { text: 'Financial Report', path: '/dashboard/financial-report' },
          { text: 'Membership Report', path: '/dashboard/membership-report' },
          { text: 'Events Data', path: '/dashboard/reports/events-data' },
        ],
      },
    ],
  },
  {
    label: 'System',
    items: [
      {
        text: 'Settings',
        icon: <SettingsIcon fontSize="small" />,
        path: '/dashboard/profile',
        permission: null,
      },
    ],
  },
];

function Wordmark({ size = 16 }) {
  return (
    <Typography
      sx={{
        fontFamily: tokens.fontDisplay,
        fontWeight: 700,
        letterSpacing: '0.14em',
        color: '#F3F6F4',
        fontSize: size,
      }}
    >
      ATU
      <Box component="span" sx={{ color: tokens.accent }}>
        ·
      </Box>
      TEIN
    </Typography>
  );
}

export default function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [searchQ, setSearchQ] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { staff, logout } = useAuth();
  const { run: runTour, startTour, shouldRunFirstTime } = useAdminTour();
  const [runFirstTime, setRunFirstTime] = useState(false);

  const hasPermission = (permission) => {
    if (!permission) return true;
    const perms = staff?.permissions || [];
    return perms.includes('all') || perms.includes(permission);
  };

  const visibleSections = menuSections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => hasPermission(item.permission)),
    }))
    .filter((section) => section.items.length > 0);

  useEffect(() => {
    if (staff && shouldRunFirstTime() && !runFirstTime) {
      setRunFirstTime(true);
      const t = setTimeout(() => startTour(), 200);
      return () => clearTimeout(t);
    }
  }, [staff, shouldRunFirstTime, runFirstTime, startTour]);

  useEffect(() => {
    const path = location.pathname;
    const expanded = {};
    visibleSections.forEach((section) => {
      section.items.forEach((item) => {
        if (item.children) {
          const hasActiveChild = item.children.some(
            (child) => path === child.path || path.startsWith(child.path + '/')
          );
          if (hasActiveChild) expanded[item.text] = true;
        }
      });
    });
    setExpandedMenus(expanded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const collapseAllMenus = () => setExpandedMenus({});

  const handleMenuClick = (item) => {
    if (item.children) {
      setExpandedMenus((prev) => {
        const isCurrentlyOpen = prev[item.text];
        if (isCurrentlyOpen) {
          const next = { ...prev };
          delete next[item.text];
          return next;
        }
        return { [item.text]: true };
      });
    } else {
      navigate(item.path);
      setMobileOpen(false);
      collapseAllMenus();
    }
  };

  const handleSubMenuClick = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const navItemSx = (active) => ({
    color: active ? '#fff' : '#C7D2CB',
    bgcolor: active ? 'rgba(255,255,255,0.06)' : 'transparent',
    borderLeft: '3px solid',
    borderLeftColor: active ? tokens.accent : 'transparent',
    borderRadius: 0,
    py: 1,
    px: 2.5,
    fontWeight: active ? 600 : 400,
    '&:hover': {
      color: '#fff',
      bgcolor: 'rgba(255,255,255,0.04)',
    },
    '&.Mui-selected': {
      color: '#fff',
      bgcolor: 'rgba(255,255,255,0.06)',
    },
    '&.Mui-selected:hover': {
      bgcolor: 'rgba(255,255,255,0.08)',
    },
  });

  const drawer = (
    <Box
      data-tour="sidebar"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: tokens.ink,
        color: '#C7D2CB',
      }}
    >
      <Box sx={{ px: 2.75, pt: 2.5, pb: 2.75 }}>
        <Wordmark />
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          pb: 2,
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(255,255,255,0.18) transparent',
          '&::-webkit-scrollbar': {
            width: 6,
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(255,255,255,0.18)',
            borderRadius: 99,
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: 'rgba(255,255,255,0.28)',
          },
        }}
      >
        {visibleSections.map((section) => (
          <Box key={section.label}>
            <Typography
              sx={{
                fontSize: 11,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                color: '#6F7E76',
                px: 2.75,
                pt: 2,
                pb: 0.75,
              }}
            >
              {section.label}
            </Typography>
            <List disablePadding>
              {section.items.map((item) => {
                const active = item.path ? isActive(item.path, item.exact) : false;
                const childActive = item.children?.some(
                  (c) => location.pathname === c.path || location.pathname.startsWith(c.path + '/')
                );
                return (
                  <React.Fragment key={item.text}>
                    <ListItem disablePadding>
                      <ListItemButton
                        onClick={() => handleMenuClick(item)}
                        selected={active || !!childActive}
                        sx={navItemSx(active || !!childActive)}
                      >
                        <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.text}
                          primaryTypographyProps={{ fontSize: 14, fontWeight: 'inherit' }}
                        />
                        {item.children &&
                          (expandedMenus[item.text] ? (
                            <ExpandLessIcon fontSize="small" />
                          ) : (
                            <ExpandMoreIcon fontSize="small" />
                          ))}
                      </ListItemButton>
                    </ListItem>
                    {item.children && (
                      <Collapse in={expandedMenus[item.text]} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                          {item.children.map((child) => {
                            const childOn = isActive(child.path);
                            return (
                              <ListItemButton
                                key={child.path}
                                sx={{
                                  ...navItemSx(childOn),
                                  pl: 6,
                                  py: 0.85,
                                }}
                                onClick={() => handleSubMenuClick(child.path)}
                                selected={childOn}
                              >
                                <ListItemText
                                  primary={child.text}
                                  primaryTypographyProps={{ fontSize: 13.5 }}
                                />
                              </ListItemButton>
                            );
                          })}
                        </List>
                      </Collapse>
                    )}
                  </React.Fragment>
                );
              })}
            </List>
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          px: 2.75,
          py: 2,
          borderTop: '1px solid rgba(255,255,255,0.08)',
          fontSize: 12,
          color: '#8A978F',
        }}
      >
        Signed in as
        <Box component="div" sx={{ color: '#F3F6F4', fontWeight: 600, mt: 0.25 }}>
          {staff?.first_name} {staff?.last_name}
        </Box>
        <Box component="div" sx={{ mt: 0.25 }}>
          {staff?.email}
        </Box>
      </Box>
    </Box>
  );

  const initials =
    `${staff?.first_name?.[0] || ''}${staff?.last_name?.[0] || ''}`.toUpperCase() || 'U';

  const bottomNavValue = (() => {
    const p = location.pathname;
    if (p === '/dashboard' || p === '/dashboard/') return 0;
    if (p.startsWith('/dashboard/members')) return 1;
    if (p.startsWith('/dashboard/hr/elections')) return 2;
    if (
      p.includes('report') ||
      p.startsWith('/dashboard/general-report') ||
      p.startsWith('/dashboard/financial-report') ||
      p.startsWith('/dashboard/membership-report')
    )
      return 3;
    if (p.startsWith('/dashboard/profile')) return 4;
    return -1;
  })();

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const q = searchQ.trim();
    if (q) navigate(`/dashboard/members?search=${encodeURIComponent(q)}`);
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: tokens.bg }}>
      <AdminTour run={runTour} onComplete={() => {}} />
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          bgcolor: tokens.surface,
          color: tokens.ink,
          borderBottom: `1px solid ${tokens.border}`,
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', minHeight: { xs: 56, sm: 64 }, gap: 2, px: { xs: 1.75, sm: 3.5 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
            <IconButton
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ display: { md: 'none' }, color: tokens.ink }}
            >
              <MenuIcon />
            </IconButton>
            <Box
              component="form"
              onSubmit={handleSearchSubmit}
              sx={{ flex: 1, maxWidth: 420, display: { xs: 'none', sm: 'block' } }}
            >
              <TextField
                size="small"
                fullWidth
                placeholder="Search members, records, elections…"
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: tokens.muted, fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: tokens.bg,
                    borderRadius: '8px',
                    '& fieldset': { borderColor: tokens.border },
                    '&:hover fieldset': { borderColor: tokens.border },
                    '&.Mui-focused fieldset': { borderColor: tokens.primary },
                    '&.Mui-focused': {
                      outline: `2px solid ${tokens.accent}`,
                      outlineOffset: 1,
                    },
                  },
                }}
              />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TourHelpButton
              onClick={startTour}
              sx={{
                display: { xs: 'none', sm: 'flex' },
                color: tokens.ink,
                borderColor: tokens.border,
                '&:hover': { borderColor: tokens.primary, bgcolor: tokens.primarySoft },
              }}
            />
            <Box
              component={Link}
              to="/dashboard/profile"
              data-tour="profile"
              sx={{
                display: { xs: 'none', md: 'block' },
                mr: 0.5,
                textAlign: 'right',
                textDecoration: 'none',
                color: 'inherit',
                lineHeight: 1.25,
              }}
            >
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                {staff?.first_name} {staff?.last_name}
              </Typography>
              <Typography sx={{ fontSize: 12, color: tokens.muted }}>
                {staff?.email}
              </Typography>
            </Box>
            <IconButton
              component={Link}
              to="/dashboard/profile"
              data-tour="profile"
              sx={{ p: 0.5 }}
              aria-label="Account"
            >
              <Avatar
                sx={{
                  width: 34,
                  height: 34,
                  bgcolor: tokens.primary,
                  color: '#fff',
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                {initials}
              </Avatar>
            </IconButton>
            <IconButton aria-label="logout" onClick={handleLogout} sx={{ color: tokens.muted }}>
              <LogoutIcon fontSize="small" />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              bgcolor: tokens.ink,
              border: 'none',
              scrollbarWidth: 'thin',
              scrollbarColor: 'rgba(255,255,255,0.18) transparent',
              '&::-webkit-scrollbar': { width: 6 },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(255,255,255,0.18)',
                borderRadius: 99,
              },
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              bgcolor: tokens.ink,
              border: 'none',
              scrollbarWidth: 'thin',
              scrollbarColor: 'rgba(255,255,255,0.18) transparent',
              '&::-webkit-scrollbar': { width: 6 },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(255,255,255,0.18)',
                borderRadius: 99,
              },
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        onClick={collapseAllMenus}
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          mt: { xs: '56px', sm: '64px' },
          pb: { xs: '80px', md: 0 },
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            maxWidth: 1240,
            width: '100%',
            mx: 'auto',
            px: { xs: 2, md: 3.5 },
            py: { xs: 2.25, md: 3.5 },
          }}
        >
          <Breadcrumbs />
          <StaffNotificationListener />
          <SystemNoticeBanner />
          <Outlet />
        </Box>
      </Box>

      <Paper
        elevation={0}
        sx={{
          display: { xs: 'block', md: 'none' },
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 40,
          borderTop: `1px solid ${tokens.border}`,
          borderRadius: 0,
          pb: 'env(safe-area-inset-bottom)',
        }}
      >
        <BottomNavigation
          showLabels
          value={bottomNavValue >= 0 ? bottomNavValue : false}
          onChange={(_, v) => {
            const routes = [
              '/dashboard',
              '/dashboard/members',
              '/dashboard/hr/elections',
              '/dashboard/membership-report',
              '/dashboard/profile',
            ];
            if (routes[v]) navigate(routes[v]);
          }}
          sx={{
            height: 64,
            '& .MuiBottomNavigationAction-root': {
              color: tokens.muted,
              minWidth: 0,
              fontSize: 11,
              '&.Mui-selected': { color: tokens.primary, fontWeight: 600 },
            },
          }}
        >
          <BottomNavigationAction label="Home" icon={<DashboardIcon />} />
          <BottomNavigationAction label="Members" icon={<PersonIcon />} />
          <BottomNavigationAction label="Elections" icon={<HowToVoteIcon />} />
          <BottomNavigationAction label="Reports" icon={<AssessmentIcon />} />
          <BottomNavigationAction label="More" icon={<SettingsIcon />} />
        </BottomNavigation>
      </Paper>
    </Box>
  );
}
